import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  StackProps,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import { ResellerProfileInterface } from "../../constants/ResellerProfile";
import useCustomToast from "../../lib/useCustomToast";
import { axiosReseller } from "../../utils/AxiosInstance";
import { ButtonPrimary } from "../button/ButtonPrimary";
import { UrlInput } from "../input/UrlInput";
import { CardSkeleton } from "../skeleton/CardSkeleton";

interface Props extends StackProps {
  data: ResellerProfileInterface | undefined;
  loaded: boolean;
  onRefresh: VoidFunction;
}

export const TabResellerProfile = ({
  data,
  loaded,
  onRefresh,
  ...rest
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();

  const initialValues = {
    storeName: data && data.name,
    storeUrl: data && data.prefix,
    storeDescription: data && data.description,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      storeName: yup.string().required("Nama store harus diisi"),
      storeUrl: yup.string().required("URL store harus diisi"),
      storeDescription: yup.string().required("Deskripsi store harus diisi"),
    }),
    onSubmit: (values) => {
      setLoading(true);

      const request = {
        name: values.storeName,
        prefix: values.storeUrl,
        description: values.storeDescription,
      };

      axiosReseller
        .put(`/v1/reseller/update-some-profile`, request)
        .then((res) => {
          onRefresh();

          toast({
            title: res.data.message,
            status: "success",
          });
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <form
      id="profileResellerForm"
      onSubmit={formik.handleSubmit}
      style={{ width: "100%" }}
    >
      <VStack w={"100%"} gap={6} align={"stretch"} {...rest}>
        <FormControl
          isInvalid={
            formik.errors.storeName && formik.touched.storeName ? true : false
          }
        >
          <FormLabel htmlFor="storeName">Nama Store</FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <Input
              name="storeName"
              type="text"
              autoComplete="off"
              onChange={formik.handleChange}
              value={formik.values.storeName || ""}
            />
          )}
          <FormErrorMessage>{formik.errors.storeName}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.storeUrl && formik.touched.storeUrl ? true : false
          }
        >
          <FormLabel htmlFor="storeUrl">URL Store</FormLabel>

          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <UrlInput
              inputValue={formik.values.storeUrl}
              name="storeUrl"
              onConfirm={(url) => {
                formik.setFieldValue("storeUrl", url);
              }}
            />
          )}
          <Text textStyle={"b-sm"} variant={"secondary"} mt={1} as={"i"}>
            *Kamu bisa custom link toko reseller kamu sendiri
          </Text>

          <FormErrorMessage>{formik.errors.storeUrl}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.storeDescription && formik.touched.storeDescription
              ? true
              : false
          }
        >
          <FormLabel htmlFor="storeDescription">Deskripsi Store</FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <Textarea
              name="storeDescription"
              autoComplete="off"
              onChange={formik.handleChange}
              value={formik.values.storeDescription || ""}
            />
          )}

          <FormErrorMessage>{formik.errors.storeDescription}</FormErrorMessage>
        </FormControl>

        <ButtonPrimary
          form="profileResellerForm"
          type="submit"
          isLoading={loading}
          isDisabled={loaded ? false : true}
          loadingText={"Loading"}
          w={"fit-content"}
        >
          {!!data?.name && !!data?.description && !!data?.prefix
            ? "Ubah Profil"
            : "Lengkapi Profil"}
        </ButtonPrimary>
      </VStack>
    </form>
  );
};
